<script setup>
import { computed, onMounted, ref } from 'vue';
import i18next from 'i18next';
import { formatDateByRegion } from '../../helpers.js';
import { fetchData } from '../../fetchData.js';

/**
 * @typedef {Object} SpecialReport
 * @property {string} title - Der Titel des Sonderberichts
 * @property {string} date - Das Veröffentlichungsdatum im Format YYYY-MM-DD
 * @property {string} image - Die URL zum Bild des Berichts
 */

// Reactive state
const reports = ref([]);
const loading = ref(false);
const error = ref(null);

// Get current year from client
const currentYear = new Date().getFullYear();

/**
 * Filters reports to only include entries from the current year
 * @param {SpecialReport[]} allReports
 * @returns {SpecialReport[]}
 */
const filterCurrentYear = (allReports) => {
    return allReports.filter((report) => {
        const reportYear = new Date(report.date).getFullYear();

        return reportYear === currentYear;
    });
};

/**
 * Computed property: Filtered and sorted reports for current year
 */
const currentYearReports = computed(() => {
    if (reports.value.length === 0) {
        return [];
    }

    return filterCurrentYear(reports.value);
});

/**
 * Computed property: Timeline items (no arrow logic, just the reports)
 */
const timelineItems = computed(() => {
    return currentYearReports.value;
});

/**
 * Computed property: Determines the next upcoming report
 * Algorithm:
 * 1. Get today's date (without time)
 * 2. Find the first report where report.date >= today
 * 3. If no future reports exist, return null
 */
const nextReport = computed(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to compare only dates

    return (
        currentYearReports.value.find((report) => {
            const reportDate = new Date(report.date);
            reportDate.setHours(0, 0, 0, 0);

            return reportDate >= today;
        }) || null
    );
});

/**
 * Checks if a report is the next upcoming report
 * @param {SpecialReport} report
 * @returns {boolean}
 */
const isNextReport = (report) => {
    return !!nextReport.value && report.date === nextReport.value.date && report.title === nextReport.value.title;
};

/**
 * Checks if a report date is in the past
 * @param {SpecialReport} report
 * @returns {boolean}
 */
const isPastReport = (report) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const reportDate = new Date(report.date);
    reportDate.setHours(0, 0, 0, 0);

    return reportDate < today;
};

/**
 * Fetches special reports from API
 */
const fetchReports = async () => {
    loading.value = true;
    error.value = null;

    try {
        const response = await fetchData('/api/v1/contentstreams/special-reports/timeline');

        reports.value = response.result.data;
    } catch (err) {
        error.value = i18next.t('reports_could_not_be_loaded_please_try_again_later');
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
    fetchReports();
});
</script>

<template>
    <div v-if="reports.length > 0" class="special-reports-timeline w-full">
        <!-- Loading State -->
        <div v-if="loading" class="flex items-center justify-center min-h-[300px]">
            <div class="text-center">
                <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue"></div>
                <p class="mt-4 text-gray-600">{{ $t('loading_reports') }}</p>
            </div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="flex items-center justify-center min-h-[300px]">
            <div class="text-center text-red-600">
                <i class="fi fi-rr-cross-circle text-5xl mb-4"></i>
                <p class="text-lg">{{ error }}</p>
            </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="currentYearReports.length === 0" class="flex items-center justify-center min-h-[300px]">
            <div class="text-center text-gray-500">
                <i class="fi fi-rr-calendar text-5xl mb-4"></i>
                <p class="text-lg">{{ $t('for_the_current_year_currentyear_no_special_reports_are_available', { currentYear: currentYear }) }}</p>
            </div>
        </div>

        <!-- Horizontal Timeline Flow -->
        <div v-else class="timeline-flow-container">
            <!-- Header -->
            <div class="mb-6">
                <h2 class="text-2xl font-bold text-gray-800 mb-2">{{ $t('publishing_schedule') }} {{ currentYear }}</h2>
                <p v-if="nextReport" class="text-sm text-gray-600">
                    {{ $t('next_reports') }}: <strong>{{ formatDateByRegion($moment(nextReport.date)) }}</strong>
                </p>
                <p v-else class="text-sm text-gray-600">{{ $t('all_reports_of_the_year_have_already_been_published') }}</p>
            </div>

            <!-- Desktop: Grid Layout (5 columns in one row) with Timeline Line -->
            <div class="hidden md:block">
                <div class="relative w-full px-4">
                    <!-- Cards Grid (5 columns, one row) -->
                    <div class="grid grid-cols-5 md:grid-cols-3 xl:grid-cols-5 gap-x-4 gap-y-4 relative z-10">
                        <!-- Timeline Line (horizontal across all cards, positioned after first render) -->
                        <div class="absolute inset-0 pointer-events-none z-0 top-0">
                            <div class="absolute left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-blue-200 via-blue-400 to-blue-200" style="top: calc(56.25% - 20px)"></div>
                        </div>

                        <div v-for="(item, index) in timelineItems" :key="index" class="relative">
                            <!-- Report Card -->
                            <router-link to="#specialreportsgrid" class="block h-full">
                                <div
                                    class="timeline-card"
                                    :class="{
                                        'ring-blue-500': isNextReport(item),
                                        'opacity-75': isPastReport(item) && !isNextReport(item)
                                    }"
                                >
                                    <!-- Next Report Badge -->
                                    <div
                                        v-if="isNextReport(item)"
                                        class="absolute w-max -top-2 left-1/2 transform -translate-x-1/2 z-20 bg-blue text-white px-2 py-0.5 rounded-full text-xs font-bold shadow-xl"
                                    >
                                        <i class="fi fi-tr-newspaper mr-1 text-xs"></i>
                                        {{ $t('next_reports') }}
                                    </div>

                                    <!-- Card Content -->
                                    <div class="bg-white rounded-lg shadow-xl hover:shadow-md transition-all duration-300 overflow-hidden h-full flex flex-col">
                                        <!-- Image -->
                                        <div class="w-full aspect-[16/9] bg-gradient-to-br from-blue-50 to-blue-100 overflow-hidden relative">
                                            <img v-if="item.image" :src="item.image" :alt="item.title" class="w-full h-full object-cover transition-transform duration-300" />
                                            <div v-else class="w-full h-full flex items-center justify-center">
                                                <i class="fi fi-tr-newspaper text-4xl text-blue-300"></i>
                                            </div>

                                            <!-- Status Badge -->
                                            <div class="absolute top-2 right-2">
                                                <span
                                                    v-if="isPastReport(item) && !isNextReport(item)"
                                                    class="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-800 bg-opacity-80 text-white text-[10px] font-semibold"
                                                >
                                                    <i class="fi fi-rr-check-circle mr-1 text-[10px]"></i>
                                                    {{ $t('published') }}
                                                </span>
                                                <span
                                                    v-else-if="!isPastReport(item) && !isNextReport(item)"
                                                    class="inline-flex items-center px-2 py-0.5 rounded-full bg-green-600 bg-opacity-90 text-white text-[10px] font-semibold"
                                                >
                                                    <i class="fi fi-rr-clock mr-1 text-[10px]"></i>
                                                    {{ $t('planned') }}
                                                </span>
                                            </div>
                                        </div>

                                        <!-- Content -->
                                        <div class="p-4 flex-1 flex flex-col">
                                            <!-- Date with emphasis -->
                                            <div class="flex items-center justify-center mb-2">
                                                <div class="text-center bg-blue-50 rounded-lg py-1.5 px-3 w-full">
                                                    <i class="fi fi-rr-calendar text-blue text-base"></i>
                                                    <p class="text-base font-bold text-blue leading-tight mt-0.5">{{ formatDateByRegion($moment(item.date)) }}</p>
                                                </div>
                                            </div>

                                            <!-- Title -->
                                            <h3 class="text-sm font-bold text-gray-800 text-center flex-1 line-clamp-3 leading-snug">
                                                {{ item.title }}
                                            </h3>

                                            <!-- Topics/Categories (if available) -->
                                            <div v-if="item.topics && item.topics.length > 0" class="flex flex-wrap gap-1 justify-center mt-2">
                                                <span
                                                    v-for="(topic, topicIndex) in item.topics"
                                                    :key="topicIndex"
                                                    class="inline-flex items-center px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-700 text-[10px]"
                                                >
                                                    {{ topic }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Mobile: Vertical Timeline -->
            <div class="md:hidden relative">
                <!-- Vertical Timeline Line -->
                <div class="absolute left-4 top-6 bottom-6 w-px bg-gradient-to-b from-blue-200 via-blue-400 to-blue-200"></div>

                <!-- Cards with Nodes -->
                <div class="space-y-6">
                    <div v-for="(item, index) in timelineItems" :key="index" class="relative">
                        <!-- Report Card -->
                        <div
                            class="bg-white rounded-lg shadow-xl overflow-hidden"
                            :class="{
                                'ring-1 ring-blue-500': isNextReport(item),
                                'opacity-75': isPastReport(item) && !isNextReport(item)
                            }"
                        >
                            <!-- Next Report Badge -->
                            <div v-if="isNextReport(item)" class="bg-blue text-white text-center py-2 px-3 font-bold text-xs">
                                <i class="fi fi-tr-newspaper mr-1 text-xs"></i>
                                {{ $t('next_reports') }}
                            </div>

                            <!-- Image -->
                            <div class="w-full aspect-[16/9] bg-gradient-to-br from-blue-50 to-blue-100 overflow-hidden relative">
                                <img v-if="item.image" :src="item.image" :alt="item.title" class="w-full h-full object-cover" />
                                <div v-else class="w-full h-full flex items-center justify-center">
                                    <i class="fi fi-tr-newspaper text-4xl text-blue-300"></i>
                                </div>

                                <!-- Status Badge -->
                                <div class="absolute top-2 right-2">
                                    <span
                                        v-if="isPastReport(item) && !isNextReport(item)"
                                        class="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-800 bg-opacity-80 text-white text-[10px] font-semibold"
                                    >
                                        <i class="fi fi-rr-check-circle mr-1 text-[10px]"></i>
                                        {{ $t('published') }}
                                    </span>
                                    <span
                                        v-else-if="!isPastReport(item) && !isNextReport(item)"
                                        class="inline-flex items-center px-2 py-0.5 rounded-full bg-green-600 bg-opacity-90 text-white text-[10px] font-semibold"
                                    >
                                        <i class="fi fi-rr-clock mr-1 text-[10px]"></i>
                                        {{ $t('planned') }}
                                    </span>
                                </div>
                            </div>

                            <!-- Content -->
                            <div class="p-3">
                                <!-- Date -->
                                <div class="flex items-center justify-center mb-2">
                                    <div class="text-center bg-blue-50 rounded-lg py-1.5 px-3 w-full">
                                        <i class="fi fi-rr-calendar text-blue text-sm"></i>
                                        <p class="text-sm font-bold text-blue leading-tight mt-0.5">{{ formatDateByRegion($moment(item.date)) }}</p>
                                    </div>
                                </div>

                                <!-- Title -->
                                <h3 class="text-xs font-bold text-gray-800 text-center leading-snug">
                                    {{ item.title }}
                                </h3>

                                <!-- Topics/Categories -->
                                <div v-if="item.topics && item.topics.length > 0" class="flex flex-wrap gap-1 justify-center mt-2">
                                    <span
                                        v-for="(topic, topicIndex) in item.topics"
                                        :key="topicIndex"
                                        class="inline-flex items-center px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-700 text-[9px]"
                                    >
                                        {{ topic }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Timeline Card */
.timeline-card {
    width: 100%;
    transition: all 0.3s ease;
}

.timeline-card:hover {
    transform: translateY(-2px);
}

/* Line clamp utility */
.line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

/* Smooth transitions */
.timeline-flow-container * {
    transition-property: all;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
